// The contrast method and its settings, shared by the Programs editor and the clinic's
// session setup. Interleaved staircases (the default) or the chart's descending levels.

import { useState } from "react";
import type { HelpContext } from "../help/types";
import { DEFAULT_STAIRCASES, staircaseStartsLogCs, type ContrastConfig, type StaircaseType } from "../lib/protocol";
import { Help } from "./Help";

export const STAIRCASE_TYPES: Array<[StaircaseType, string, string]> = [
  ["weighted", "Weighted up/down (Kaernbach)",
    "1-up/1-down with the up step = p/(1−p) × the down step; converges on p correct (default 75%)."],
  ["quest", "QUEST (Bayesian)",
    "Each trial at the posterior mean of the threshold; threshold = the 61.5%-correct point for a 4-way E."],
  ["levitt", "Levitt n-down/1-up",
    "Harder after n right in a row, easier after one wrong: 3-down/1-up converges on 79.4% correct."],
  ["simple", "1-up/1-down",
    "Harder after each right answer, easier after each wrong one: converges on 50% correct."],
];

export function staircaseTypeLabel(t: StaircaseType | undefined): string {
  return STAIRCASE_TYPES.find(([k]) => k === t)?.[1] ?? String(t ?? "");
}

/** A number input that lets the user type freely (no clamping on each keystroke: typing
 *  "20" must not pass through 5); a value in range is used at once, anything else is
 *  clamped when the field is left or Enter is pressed. `scale` shows value × scale. */
export function NumberField({ label, value, min, max, step, integer = false, scale = 1, onCommit, help, ctx }: {
  label: string; value: number; min: number; max: number; step: number; integer?: boolean; scale?: number;
  onCommit: (v: number) => void; help?: string; ctx?: HelpContext;
}) {
  const [text, setText] = useState<string | null>(null);
  const ok = (v: number) => v >= min && v <= max && (!integer || Number.isInteger(v));
  const finish = (raw: string) => {
    const v = Number(raw);
    if (raw.trim() !== "" && Number.isFinite(v)) {
      let x = Math.min(max, Math.max(min, v));
      if (integer) x = Math.round(x);
      onCommit(x / scale);
    }
    setText(null);
  };
  return (
    <label><span>{label}{help && <Help k={help} ctx={ctx} />}</span>
      <input type="number" aria-label={label} min={min} max={max} step={step}
        value={text ?? String(Math.round(value * scale * 1e4) / 1e4)}
        onChange={(e) => {
          const raw = e.target.value;
          setText(raw);
          const v = Number(raw);
          if (raw.trim() !== "" && Number.isFinite(v) && ok(v)) onCommit(v / scale);
        }}
        onBlur={(e) => finish(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") finish((e.target as HTMLInputElement).value); }} />
    </label>
  );
}

/** Seconds per contrast trial for a low-vision patient, for the time estimate. */
const SECONDS_PER_TRIAL = 3;

export function ContrastMethodFields({ value, onChange, compact = false }: {
  value: ContrastConfig; onChange: (c: ContrastConfig) => void; compact?: boolean;
}) {
  const C: ContrastConfig = { ...DEFAULT_STAIRCASES, ...value };
  const set = (patch: Partial<ContrastConfig>) => onChange({ ...C, ...patch });
  const ctx: HelpContext = { contrast: C };
  const settled = C.stop_rule === "settled";
  const staircaseTrials = C.n_staircases * C.trials_per_staircase;
  // attention checks come on top: on average rate / (1 - rate) per staircase trial (never two in a row)
  const total = Math.round(staircaseTrials * (1 + C.catch_rate / Math.max(0.01, 1 - C.catch_rate)));
  const minutes = Math.round((total * SECONDS_PER_TRIAL) / 60 * 2) / 2;
  const typeHelp = STAIRCASE_TYPES.find(([k]) => k === C.staircase_type)?.[2];
  return (
    <>
      <label><span>Contrast method<Help k="contrast.method" ctx={ctx} /></span>
        <select aria-label="Contrast method" value={C.method}
          onChange={(e) => set({ method: e.target.value as ContrastConfig["method"] })}>
          <option value="staircases">Interleaved staircases (averaged)</option>
          <option value="pelli_robson">Chart-style descending levels (Pelli-Robson)</option>
          {C.method === "quest" && <option value="quest">QUEST pair (older programs)</option>}
        </select>
      </label>
      {C.method === "staircases" && (
        <>
          <label><span>Staircase type<Help k="contrast.staircase_type" ctx={ctx} /></span>
            <select aria-label="Staircase type" value={C.staircase_type}
              onChange={(e) => set({ staircase_type: e.target.value as StaircaseType })}>
              {STAIRCASE_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </label>
          <NumberField help="contrast.n_staircases" ctx={ctx} label="Number of staircases" value={C.n_staircases} min={1} max={8} step={1} integer
            onCommit={(v) => set({ n_staircases: v })} />
          <NumberField help="contrast.trials_per_staircase" ctx={ctx} label={settled ? "Most trials per staircase" : "Trials per contrast staircase"}
            value={C.trials_per_staircase} min={5} max={60} step={1} integer
            onCommit={(v) => set({ trials_per_staircase: v })} />
          {C.staircase_type === "weighted" && (
            <NumberField help="contrast.weighted_target" ctx={ctx} label="Target % correct" value={C.weighted_target} scale={100} min={30} max={95} step={5}
              onCommit={(v) => set({ weighted_target: v })} />
          )}
          {C.staircase_type === "levitt" && (
            <label><span>Right in a row to step down<Help k="contrast.levitt_n_down" ctx={ctx} /></span>
              <select aria-label="Right in a row to step down" value={C.levitt_n_down}
                onChange={(e) => set({ levitt_n_down: Number(e.target.value) })}>
                <option value={2}>2 (70.7% correct)</option>
                <option value={3}>3 (79.4% correct)</option>
                <option value={4}>4 (84.1% correct)</option>
              </select>
            </label>
          )}
          {!compact && (
            <>
              <NumberField help="contrast.start_log_cs_easy" ctx={ctx} label="Easiest start (log CS)" value={C.start_log_cs_easy} min={0} max={3} step={0.05}
                onCommit={(v) => set({ start_log_cs_easy: v })} />
              <NumberField help="contrast.start_log_cs_hard" ctx={ctx} label="Hardest start (log CS)" value={C.start_log_cs_hard} min={0} max={3} step={0.05}
                onCommit={(v) => set({ start_log_cs_hard: v })} />
              <NumberField help="contrast.step_log_cs" ctx={ctx} label="First step (log units)" value={C.step_log_cs} min={0.05} max={1} step={0.05}
                onCommit={(v) => set({ step_log_cs: v })} />
              <label><span>Starts<Help k="contrast.stagger" ctx={ctx} /></span>
                <select aria-label="Staircase starts" value={C.stagger}
                  onChange={(e) => set({ stagger: e.target.value as ContrastConfig["stagger"] })}>
                  <option value="none">all at once</option>
                  <option value="fixed">staggered, evenly spaced</option>
                  <option value="random">staggered, random gaps</option>
                </select>
              </label>
              {C.stagger !== "none" && (
                <NumberField help="contrast.stagger_trials" ctx={ctx} label={C.stagger === "fixed" ? "Trials between starts" : "Mean trials between starts"}
                  value={C.stagger_trials} min={1} max={20} step={1} integer onCommit={(v) => set({ stagger_trials: v })} />
              )}
              <label><span>Picking<Help k="contrast.pick" ctx={ctx} /></span>
                <select aria-label="Staircase picking" value={C.pick}
                  onChange={(e) => set({ pick: e.target.value as ContrastConfig["pick"] })}>
                  <option value="random">random</option>
                  <option value="balanced">balanced (each once per round, no repeats in a row)</option>
                </select>
              </label>
              <label><span>Stop<Help k="contrast.stop_rule" ctx={ctx} /></span>
                <select aria-label="Staircase stop" value={C.stop_rule}
                  onChange={(e) => set({ stop_rule: e.target.value as ContrastConfig["stop_rule"] })}>
                  <option value="trials">after the set number of trials</option>
                  <option value="settled">when settled (or at the most trials)</option>
                </select>
              </label>
              {settled && C.staircase_type !== "quest" && (
                <NumberField help="contrast.stop_reversals" ctx={ctx} label="Reversals to settle" value={C.stop_reversals} min={2} max={20} step={1} integer
                  onCommit={(v) => set({ stop_reversals: v })} />
              )}
              {settled && C.staircase_type === "quest" && (
                <NumberField help="contrast.stop_sd_log" ctx={ctx} label="Settled at posterior SD (log)" value={C.stop_sd_log} min={0.02} max={0.5} step={0.01}
                  onCommit={(v) => set({ stop_sd_log: v })} />
              )}
              <NumberField help="contrast.catch_rate" ctx={ctx} label="Attention checks (%)" value={C.catch_rate} scale={100} min={0} max={30} step={1}
                onCommit={(v) => set({ catch_rate: v })} />
              {C.catch_rate > 0 && (
                <>
                  <NumberField help="contrast.catch_log_cs" ctx={ctx} label="Check contrast (log CS)" value={C.catch_log_cs} min={0} max={1.5} step={0.1}
                    onCommit={(v) => set({ catch_log_cs: v })} />
                  <NumberField help="contrast.catch_min_pct" ctx={ctx} label="Checks right at least (%)" value={C.catch_min_pct} min={0} max={100} step={5}
                    onCommit={(v) => set({ catch_min_pct: v })} />
                  <label><span>If fewer<Help k="contrast.catch_action" ctx={ctx} /></span>
                    <select aria-label="If fewer checks right" value={C.catch_action}
                      onChange={(e) => set({ catch_action: e.target.value as ContrastConfig["catch_action"] })}>
                      <option value="flag">flag the result</option>
                      <option value="invalid">the result does not count</option>
                    </select>
                  </label>
                </>
              )}
            </>
          )}
          <div className="small muted" style={{ flexBasis: "100%" }}>
            {C.n_staircases} staircase{C.n_staircases === 1 ? "" : "s"} taking turns {C.pick === "balanced" ? "in balanced rounds" : "at random"}
            {C.stagger !== "none" ? `, joining one by one about ${C.stagger_trials} trials apart` : ""}, starting at log CS{" "}
            {staircaseStartsLogCs(C).map((v) => v.toFixed(2)).join(", ")}; {settled ? "up to " : ""}{total} trials per eye
            {C.catch_rate > 0 ? ` including about ${total - staircaseTrials} attention checks` : ""} (about {settled ? "≤ " : ""}{minutes} min).{" "}
            {typeHelp}{C.staircase_type !== "quest" && " Steps halve at the 1st and 2nd reversal; each threshold is the mean of the later reversals."}{" "}
            The result is the mean of the staircases' thresholds; their spread is reported as a check.
            {C.start_log_cs_easy > C.start_log_cs_hard && <span className="error"> The easiest start must not be above the hardest.</span>}
          </div>
        </>
      )}
      {C.method === "pelli_robson" && (
        <label><span>Es per level<Help k="contrast.pr_group_size" ctx={ctx} /></span>
          <select aria-label="Es per level" value={`${C.pr_group_size}/${C.pr_pass_correct}`} onChange={(e) => {
            const [n, k] = e.target.value.split("/").map(Number);
            set({ pr_group_size: n!, pr_pass_correct: k! });
          }}>
            <option value="5/4">5, pass 4 (guessing passes 1.6%)</option>
            <option value="4/3">4, pass 3 (5.1%)</option>
            <option value="3/2">3, pass 2 (chart rule, 15.6%)</option>
          </select>
        </label>
      )}
    </>
  );
}
